import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Result, Row, Select, Space, Statistic, Table, Typography, Upload, message } from 'antd';
import { CloudUploadOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiErrorMessage, competencyKeys } from '@/hooks/useCompetency';
import { competencyService, type CompetencyImportKind, type CompetencyImportResult } from '@/services/competency.service';
import { triggerDownload } from '@/reports/utils/exportTable';
import CompetencyNav from './CompetencyNav';

const { Text, Paragraph } = Typography;

const KINDS: Array<{ value: CompetencyImportKind; label: string; hint: string }> = [
  {
    value: 'job-profiles',
    label: 'Job descriptions',
    hint: 'Documents columns match the Job Description form; Lines = experience & skills / competence requirements.',
  },
  { value: 'assignments', label: 'Job description assignments', hint: 'Assign a job description to many employees at once.' },
  { value: 'courses', label: 'Training courses', hint: 'Training course catalogue with category, frequency and effectiveness settings.' },
  {
    value: 'sessions',
    label: 'Training attendance',
    hint: 'Attendance sessions (NTES-QSF-HR-04 fields) with attendee lines — two sheets: Documents and Lines.',
  },
  {
    value: 'effectiveness',
    label: 'Training effectiveness',
    hint: 'NTES-QSF-HR-05 effectiveness evaluations keyed by session number (Documents + trainee Lines).',
  },
  {
    value: 'schedule',
    label: 'Annual training schedule',
    hint: 'NTES-QSF-HR-03 schedule lines: course, employee/department, trainer, frequency, Jan–Dec planned months (P).',
  },
  { value: 'training-records', label: 'Training history', hint: 'Completed training before go-live.' },
  { value: 'certifications', label: 'Certificates', hint: 'Employee certificates and licences with expiry dates.' },
  { value: 'competencies', label: 'Competency levels', hint: 'Current competency level per employee and skill.' },
  {
    value: 'assessments',
    label: 'Competency assessments',
    hint: 'NTES-QSF-HR-02 assessment / re-assessment scores and recommendations (imported as closed historical records).',
  },
];

const KIND_VALUES = new Set(KINDS.map((k) => k.value));

const CompetencyImportPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const kindFromUrl = searchParams.get('kind') as CompetencyImportKind | null;
  const initialKind =
    kindFromUrl && KIND_VALUES.has(kindFromUrl) ? kindFromUrl : ('job-profiles' as CompetencyImportKind);
  const [kind, setKind] = useState<CompetencyImportKind>(initialKind);
  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [result, setResult] = useState<CompetencyImportResult | null>(null);
  const [busy, setBusy] = useState<'template' | 'check' | 'commit' | null>(null);
  const spec = useMemo(() => KINDS.find((k) => k.value === kind)!, [kind]);

  const reset = () => {
    setFile(null);
    setFileList([]);
    setResult(null);
  };

  const selectKind = (v: CompetencyImportKind) => {
    setKind(v);
    reset();
    const next = new URLSearchParams(searchParams);
    next.set('kind', v);
    setSearchParams(next, { replace: true });
  };

  const downloadTemplate = async () => {
    setBusy('template');
    try {
      triggerDownload(
        await competencyService.downloadImportTemplate(kind),
        kind === 'job-profiles' ? 'job-descriptions-template.xlsx' : `competency-${kind}-template.xlsx`
      );
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const run = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(dryRun ? 'check' : 'commit');
    try {
      const r = await competencyService.runImport(kind, file, dryRun);
      setResult(r);
      if (!dryRun && !r.errors.length) {
        message.success(`Imported: ${r.created} created, ${r.updated} updated`);
        queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      }
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const committed = result && !result.dry_run && !result.errors.length;

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav title="Bulk import" />
      <Row gutter={16}>
        <Col xs={24} lg={9}>
          <Card title="1. Choose what to import" style={{ marginBottom: 16 }}>
            <Select
              style={{ width: '100%' }}
              value={kind}
              onChange={selectKind}
              options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
            />
            <Paragraph type="secondary" style={{ marginTop: 8 }}>
              {spec.hint}
            </Paragraph>
            <Button icon={<DownloadOutlined />} loading={busy === 'template'} onClick={downloadTemplate}>
              Download template
            </Button>
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              The template has a sample row and an Instructions sheet with the allowed values. Employees are matched by employee number, courses by
              course code and job descriptions by code.
            </Paragraph>
          </Card>
          <Card title="2. Upload and check">
            <Upload.Dragger
              accept=".xlsx,.xls"
              maxCount={1}
              fileList={fileList}
              beforeUpload={(f) => {
                setFile(f);
                setFileList([{ uid: f.uid, name: f.name, status: 'done' }]);
                setResult(null);
                return false;
              }}
              onRemove={() => reset()}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag the filled template here</p>
            </Upload.Dragger>
            <Space style={{ marginTop: 12 }}>
              <Button disabled={!file} loading={busy === 'check'} onClick={() => run(true)}>
                Check file
              </Button>
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                disabled={!file || !result?.dry_run || result.errors.length > 0 || !result.valid}
                loading={busy === 'commit'}
                onClick={() => run(false)}
              >
                Import {result?.dry_run && !result.errors.length ? `${result.valid} row(s)` : ''}
              </Button>
            </Space>
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              Nothing is saved while any row has an error. Fix the file and check again.
            </Paragraph>
          </Card>
        </Col>
        <Col xs={24} lg={15}>
          <Card title="3. Result">
            {!result && <Text type="secondary">Check a file to see the validation result here.</Text>}
            {committed && (
              <Result status="success" title="Import completed" subTitle={`${result.created} created, ${result.updated} updated`} extra={<Button onClick={reset}>Import another file</Button>} />
            )}
            {result && !committed && (
              <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <Statistic title="Rows" value={result.total} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Valid" value={result.valid} valueStyle={{ color: '#389e0d' }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Errors" value={result.errors.length} valueStyle={{ color: result.errors.length ? '#cf1322' : undefined }} />
                  </Col>
                </Row>
                {result.errors.length ? (
                  <Table
                    rowKey={(r, i) => `${r.row}-${i}`}
                    size="small"
                    dataSource={result.errors}
                    pagination={{ defaultPageSize: 20 }}
                    columns={[
                      { title: 'Row', dataIndex: 'row', width: 80 },
                      { title: 'Problem', dataIndex: 'message' },
                    ]}
                  />
                ) : (
                  <Alert type="success" showIcon message={`All ${result.valid} row(s) are valid. Click Import to save them.`} />
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CompetencyImportPage;
